<template>
  <q-dialog
    v-model="localValue"
    :position="position"
    @hide="onDialogHide"
    :maximized="maximized"
    :persistent="persistent"
    :class="dialogClass"
    transition-show="scale"
    transition-hide="scale"
    seamless
  >
    <q-card class="base-dialog" :style="{ width: width }">
      <!-- Header section - only shown when there's a title and header is not explicitly hidden -->
      <q-card-section v-if="!hideHeader && title" class="base-dialog__header">
        <div>
          <h5 class="base-dialog__title">{{ title }}</h5>
          <p v-if="subtitle" class="base-dialog__subtitle">{{ subtitle }}</p>
        </div>
        <q-space />
        <q-btn v-if="!hideCloseButton" flat round icon="close" size="sm" v-close-popup class="base-dialog__close-btn" @click="$emit('close')" />
      </q-card-section>

      <q-separator v-if="!hideHeader && title" />

      <!-- Content section -->
      <q-card-section :class="{ 'q-pt-none': hideHeader || contentNoPadding }" class="base-dialog__body">
        <slot></slot>
      </q-card-section>

      <!-- Footer with actions -->
      <q-card-actions v-if="showFooter" align="right" class="base-dialog__footer">
        <slot name="footer-left"></slot>
        <div class="base-dialog__actions">
          <slot name="footer-actions">
            <q-btn v-if="showCancel" flat no-caps :label="cancelLabel" v-close-popup class="base-dialog__cancel-btn" @click="onCancel" />
            <q-btn v-if="showConfirm" :label="confirmLabel" flat no-caps :class="confirmButtonClass" @click="onConfirm" :loading="loading" :disabled="disabled || loading" />
          </slot>
        </div>
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>

<script>
/**
 * BaseDialog - A consistent dialog component for the application
 *
 * This component provides standardized dialog behavior and styling
 * that aligns with the application's design system.
 *
 * @component
 */
export default {
  name: 'BaseDialog',

  props: {
    /**
     * Controls the dialog visibility - follows v-model pattern
     */
    value: {
      type: Boolean,
      default: false
    },

    /**
     * Dialog title displayed in the header
     */
    title: {
      type: String,
      default: ''
    },

    /**
     * Optional subtitle displayed under the title
     */
    subtitle: {
      type: String,
      default: ''
    },

    /**
     * Width of the dialog - can be px, % or auto
     */
    width: {
      type: String,
      default: '550px'
    },

    /**
     * Position of the dialog - standard or top/maximized/etc
     */
    position: {
      type: String,
      default: 'standard'
    },

    /**
     * Whether to display the footer section
     */
    showFooter: {
      type: Boolean,
      default: true
    },

    /**
     * Whether to show the cancel button
     */
    showCancel: {
      type: Boolean,
      default: true
    },

    /**
     * Text for the cancel button
     */
    cancelLabel: {
      type: String,
      default: 'Cancel'
    },

    /**
     * Whether to show the confirm button
     */
    showConfirm: {
      type: Boolean,
      default: true
    },

    /**
     * Text for the confirm button
     */
    confirmLabel: {
      type: String,
      default: 'Confirm'
    },

    /**
     * CSS class for the confirm button
     */
    confirmButtonClass: {
      type: String,
      default: 'base-dialog__primary-btn'
    },

    /**
     * Whether the dialog can be dismissed by clicking outside
     */
    persistent: {
      type: Boolean,
      default: false
    },

    /**
     * Whether the dialog content section should have padding
     */
    contentNoPadding: {
      type: Boolean,
      default: false
    },

    /**
     * Whether to hide the header section completely
     */
    hideHeader: {
      type: Boolean,
      default: false
    },

    /**
     * Whether to hide the close button in the header
     */
    hideCloseButton: {
      type: Boolean,
      default: false
    },

    /**
     * Whether the dialog should take up the full screen
     */
    maximized: {
      type: Boolean,
      default: false
    },

    /**
     * Additional CSS class for the dialog
     */
    dialogClass: {
      type: String,
      default: ''
    },

    /**
     * Whether the confirm button should be disabled
     */
    disabled: {
      type: Boolean,
      default: false
    },

    /**
     * Whether to show a loading spinner on the confirm button
     */
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      localValue: this.value
    };
  },

  watch: {
    value(newVal) {
      this.localValue = newVal;
    },

    localValue(newVal) {
      this.$emit('input', newVal);

      if (newVal) {
        this.$emit('open');
      }
    }
  },

  methods: {
    /**
     * Handle dialog hide event
     */
    onDialogHide() {
      this.$emit('hide');
    },

    /**
     * Handle confirm button click
     */
    onConfirm() {
      this.$emit('confirm');
    },

    /**
     * Handle cancel button click
     */
    onCancel() {
      this.$emit('cancel');
    },

    /**
     * Manually close the dialog
     */
    close() {
      this.localValue = false;
    },

    /**
     * Manually open the dialog
     */
    open() {
      this.localValue = true;
    }
  }
};
</script>

<style lang="scss" scoped>
.base-dialog {
  min-width: 550px;
  max-width: 90vw;
  border-radius: 16px;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  will-change: transform, opacity;
  background-color: white;

  &__header {
    padding: 24px 24px;
    background: linear-gradient(to right, #f7f7f7, #ffffff);
    border-radius: 16px 16px 0 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__title {
    @include mona-sans-font($size: 20px, $bolded: true, $color: var(--main-text-color));
    letter-spacing: 0.3px;
    margin: 0;
    line-height: 1.2;
  }

  &__subtitle {
    @include inter-font($size: 14px, $color: var(--secondary-text-color));
    margin-top: 4px;
    margin-bottom: 0;
  }

  &__close-btn {
    opacity: 0.7;
    transition: opacity 0.2s ease, background-color 0.2s ease;

    &:hover {
      opacity: 1;
      background: rgba(0, 0, 0, 0.05);
    }
  }

  &__body {
    padding: 24px;
    background-color: white;
  }

  &__footer {
    padding: 16px 24px 24px;
    background-color: white;
    border-top: 1px solid rgba(0, 0, 0, 0.03);
  }

  &__actions {
    display: flex;
    gap: 12px;
    width: 100%;
    justify-content: center;
  }

  &__cancel-btn {
    background-color: var(--terziary-btn-color);
    @include inter-font($size: 14px, $bolded: true, $color: var(--main-text-color));
    height: 44px;
    border-radius: 8px;
    padding: 0 20px;
    transition: all 0.2s ease;
    width: 100%;
  }

  &__primary-btn {
    @include inter-font($size: 14px, $bolded: true, $color: var(--main-text-color));
    background-color: var(--main-btn-color);
    height: 44px;
    border-radius: 8px;
    padding: 0 20px;
    letter-spacing: 0.3px;
    transition: all 0.2s ease;
    width: 100%;

    &:hover {
      opacity: 0.9;
    }
  }

  &__negative-btn {
    @include inter-font($size: 14px, $bolded: true, $color: var(--secondary-bg-color));
    background-color: var(--negative);
    height: 44px;
    border-radius: 8px;
    padding: 0 20px;
    letter-spacing: 0.3px;
    transition: all 0.2s ease;
    width: 100%;

    &:hover {
      opacity: 0.9;
    }
  }
}
</style>
